import { products } from "./catalog";
import type { Locale } from "@/domain/schemas";

export type RouteKind =
  | "home"
  | "products"
  | "product"
  | "business"
  | "industries"
  | "industry"
  | "oem"
  | "recycling"
  | "quote"
  | "material"
  | "certifications"
  | "about"
  | "knowledge"
  | "knowledge-category"
  | "article"
  | "faq"
  | "contact"
  | "search"
  | "account"
  | "cart"
  | "checkout"
  | "checkout-result"
  | "policy"
  | "states"
  | "not-found";

export interface ResolvedRoute {
  kind: RouteKind;
  locale: Locale;
  path: string;
  category?: string;
  slug?: string;
  section?: string;
  noIndex?: boolean;
}

export interface NavigationItem {
  label: string;
  href: string;
}

export const productCategories = [
  "plate",
  "bowl",
  "lunchbox",
  "cutlery",
  "cup-bottle",
  "recycled",
] as const;

export const industries = [
  "hospitality",
  "corporate",
  "education",
  "government",
  "medical",
  "aviation",
  "outdoor",
] as const;

export const materialSlugs = ["cpet", "tritan", "sugarcane-fiber"] as const;

const accountSections = [
  "login",
  "register",
  "forgot-password",
  "overview",
  "profile",
  "security",
  "addresses",
  "orders",
  "returns",
  "wishlist",
] as const;

const aboutSections = ["story", "manufacturing", "sustainability"] as const;
const policySlugs = ["shipping", "returns-policy", "privacy", "terms"] as const;
const knowledgeCategories = ["materials", "recycling", "care"] as const;
const articleSlugs = ["cpet-guide", "tritan-guide", "closed-loop-recycling"] as const;

function prefix(locale: Locale): string {
  return locale === "en" ? "/en" : "/zh-tw";
}

export function navigationFor(locale: Locale): NavigationItem[] {
  const base = prefix(locale);
  return locale === "en"
    ? [
        { label: "Shop", href: `${base}/products` },
        { label: "Business", href: `${base}/business` },
        { label: "Materials", href: `${base}/materials/cpet` },
        { label: "Sustainability knowledge", href: `${base}/knowledge` },
        { label: "About", href: `${base}/about` },
        { label: "Contact", href: `${base}/contact` },
      ]
    : [
        { label: "選購食器", href: `${base}/products` },
        { label: "企業採購", href: `${base}/business` },
        { label: "材質與認證", href: `${base}/materials/cpet` },
        { label: "永續知識", href: `${base}/knowledge` },
        { label: "關於哩皿", href: `${base}/about` },
        { label: "聯絡我們", href: `${base}/contact` },
      ];
}

function normalizePath(input: string): string {
  const path = input.split(/[?#]/, 1)[0] || "/";
  if (path === "/") return "/";
  return `/${path.split("/").filter(Boolean).join("/")}`.replace(/\/+$/, "");
}

export function resolveRoute(input: string): ResolvedRoute {
  const path = normalizePath(input);
  const segments = path.split("/").filter(Boolean);
  const locale: Locale = segments[0] === "en" ? "en" : "zh-TW";
  const localeSegment = locale === "en" ? "en" : "zh-tw";
  const rest = segments[0] === "en" || segments[0] === "zh-tw" ? segments.slice(1) : segments;

  if (path === "/" || (segments.length === 1 && segments[0] === localeSegment)) {
    return { kind: "home", locale, path };
  }
  if (rest[0] === "products") {
    if (!rest[1]) return { kind: "products", locale, path };
    if (productCategories.includes(rest[1] as (typeof productCategories)[number])) {
      return { kind: "products", locale, path, category: rest[1] };
    }
    if (products.some((product) => product.slug === rest[1])) {
      return { kind: "product", locale, path, slug: rest[1] };
    }
  }
  if (rest[0] === "business") {
    if (!rest[1]) return { kind: "business", locale, path };
    if (rest[1] === "industries" && !rest[2]) {
      return { kind: "industries", locale, path };
    }
    if (
      rest[1] === "industries" &&
      industries.includes(rest[2] as (typeof industries)[number])
    ) {
      return { kind: "industry", locale, path, slug: rest[2] };
    }
    if (rest[1] === "oem-odm-obm") return { kind: "oem", locale, path };
    if (rest[1] === "recycling") return { kind: "recycling", locale, path };
    if (rest[1] === "quote") return { kind: "quote", locale, path, noIndex: true };
  }
  if (
    rest[0] === "materials" &&
    materialSlugs.includes(rest[1] as (typeof materialSlugs)[number])
  ) {
    return { kind: "material", locale, path, slug: rest[1] };
  }
  if (rest[0] === "certifications" && rest.length === 1) {
    return { kind: "certifications", locale, path };
  }
  if (rest[0] === "about") {
    if (!rest[1]) return { kind: "about", locale, path };
    if (aboutSections.includes(rest[1] as (typeof aboutSections)[number])) {
      return { kind: "about", locale, path, section: rest[1] };
    }
  }
  if (rest[0] === "knowledge") {
    if (!rest[1]) return { kind: "knowledge", locale, path };
    if (knowledgeCategories.includes(rest[1] as (typeof knowledgeCategories)[number])) {
      return { kind: "knowledge-category", locale, path, category: rest[1] };
    }
    if (articleSlugs.includes(rest[1] as (typeof articleSlugs)[number])) {
      return { kind: "article", locale, path, slug: rest[1] };
    }
  }
  if (rest[0] === "faq" && rest.length === 1) return { kind: "faq", locale, path };
  if (rest[0] === "contact" && rest.length === 1) {
    return { kind: "contact", locale, path, noIndex: true };
  }
  if (rest[0] === "search" && rest.length === 1) {
    return { kind: "search", locale, path, noIndex: true };
  }
  if (rest[0] === "account" && rest[1] === "orders" && rest[2]) {
    return {
      kind: "account",
      locale,
      path,
      section: "order-detail",
      slug: rest[2],
      noIndex: true,
    };
  }
  if (
    rest[0] === "account" &&
    accountSections.includes(rest[1] as (typeof accountSections)[number])
  ) {
    return {
      kind: "account",
      locale,
      path,
      section: rest[1],
      noIndex: true,
    };
  }
  if (rest[0] === "cart" && rest.length === 1) {
    return { kind: "cart", locale, path, noIndex: true };
  }
  if (rest[0] === "checkout" && rest[1] === "result") {
    return { kind: "checkout-result", locale, path, noIndex: true };
  }
  if (rest[0] === "checkout" && rest.length === 1) {
    return { kind: "checkout", locale, path, noIndex: true };
  }
  if (policySlugs.includes(rest[0] as (typeof policySlugs)[number])) {
    return { kind: "policy", locale, path, slug: rest[0] };
  }
  if (rest[0] === "states") {
    return { kind: "states", locale, path, noIndex: true };
  }
  return { kind: "not-found", locale, path, noIndex: true };
}

const localeRoutes = (locale: Locale): string[] => {
  const base = prefix(locale);
  return [
    base,
    `${base}/products`,
    ...productCategories.map((category) => `${base}/products/${category}`),
    ...products.map((product) => `${base}/products/${product.slug}`),
    `${base}/business`,
    `${base}/business/industries`,
    ...industries.map((industry) => `${base}/business/industries/${industry}`),
    `${base}/business/oem-odm-obm`,
    `${base}/business/recycling`,
    `${base}/business/quote`,
    ...materialSlugs.map((material) => `${base}/materials/${material}`),
    `${base}/certifications`,
    `${base}/about`,
    ...aboutSections.map((section) => `${base}/about/${section}`),
    `${base}/knowledge`,
    ...knowledgeCategories.map((category) => `${base}/knowledge/${category}`),
    ...articleSlugs.map((article) => `${base}/knowledge/${article}`),
    `${base}/faq`,
    `${base}/contact`,
    `${base}/search`,
    ...accountSections.map((section) => `${base}/account/${section}`),
    `${base}/account/orders/DEMO-20260724-001`,
    `${base}/cart`,
    `${base}/checkout`,
    `${base}/checkout/result`,
    ...policySlugs.map((policy) => `${base}/${policy}`),
    `${base}/states`,
  ];
};

export const prerenderRoutes = ["/", ...localeRoutes("zh-TW"), ...localeRoutes("en")];

export function switchLocalePath(path: string, locale: Locale): string {
  const normalized = normalizePath(path);
  const rest = normalized.replace(/^\/(?:zh-tw|en)(?=\/|$)/, "");
  return `${prefix(locale)}${rest || ""}`;
}
